/**
 * Terms of Service — ProfitSpot
 */
import React, { useState, useEffect } from 'react';
import { Shield, ArrowLeft, ChevronRight, AlertTriangle, FileText, CreditCard, Ban, Cpu, Scale, Mail, RefreshCw, BookOpen, Gavel } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const SECTIONS = [
  { id: 'acceptance',     title: 'Acceptance of Terms',        icon: FileText },
  { id: 'description',    title: 'Description of Service',     icon: Cpu },
  { id: 'not-advice',     title: 'Not Financial Advice',       icon: AlertTriangle },
  { id: 'non-custodial',  title: 'Non-Custodial Nature',       icon: Shield },
  { id: 'defi-risks',     title: 'DeFi & Smart Contract Risks',icon: AlertTriangle },
  { id: 'subscription',   title: 'Subscription & Billing',     icon: CreditCard },
  { id: 'trial',          title: 'Free Trial',                 icon: BookOpen },
  { id: 'prohibited',     title: 'Prohibited Uses',            icon: Ban },
  { id: 'ip',             title: 'Intellectual Property',      icon: FileText },
  { id: 'disclaimers',    title: 'Disclaimer of Warranties',   icon: Scale },
  { id: 'liability',      title: 'Limitation of Liability',    icon: Scale },
  { id: 'termination',    title: 'Termination',                icon: Ban },
  { id: 'governing',      title: 'Governing Law',              icon: Gavel },
  { id: 'changes',        title: 'Changes to Terms',           icon: RefreshCw },
  { id: 'contact',        title: 'Contact',                    icon: Mail },
];

const Section = ({ id, title, icon: Icon, children }) => (
  <section id={id} className="scroll-mt-8 border-b border-gray-800/50 pb-8 mb-8 last:border-0">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-8 h-8 rounded-lg bg-[#00ff88]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#00ff88]" />
      </div>
      <h2 className="text-base font-bold text-white">{title}</h2>
    </div>
    <div className="text-gray-400 text-sm leading-relaxed space-y-3 pl-11">
      {children}
    </div>
  </section>
);

const Callout = ({ color = 'amber', children }) => {
  const styles = {
    amber: 'bg-amber-500/5 border-amber-500/20 text-amber-400',
    red:   'bg-red-500/5 border-red-500/20 text-red-400',
    teal:  'bg-[#00ff88]/5 border-[#00ff88]/20 text-[#00ff88]',
    blue:  'bg-[#00d4ff]/5 border-[#00d4ff]/20 text-[#00d4ff]',
  };
  return (
    <div className={`border rounded-lg p-4 ${styles[color]}`}>
      {children}
    </div>
  );
};

export default function Terms() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('acceptance');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      {/* Hero header */}
      <div className="border-b border-gray-800 bg-[#0f0f1a]">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-white mb-6 transition-colors text-sm"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-[#00ff88]/10 rounded-xl border border-[#00ff88]/20">
              <Shield className="w-7 h-7 text-[#00ff88]" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Terms of Service</h1>
              <p className="text-gray-500 text-sm">Last updated: March 2026 &nbsp;·&nbsp; Effective immediately upon account creation</p>
            </div>
          </div>

          <Callout color="amber">
            <p className="font-semibold text-sm mb-1">Important Risk Disclosure</p>
            <p className="text-xs text-gray-400">
              ProfitSpot is an informational tool only. Nothing on this platform constitutes financial, investment, legal, or tax advice.
              DeFi protocols carry significant risks including smart contract exploits, impermanent loss, and total loss of funds.
              Only interact with protocols you understand, and only with capital you can afford to lose entirely.
            </p>
          </Callout>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 flex gap-10">
        {/* Sticky Table of Contents — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-8">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">Contents</p>
            <nav className="space-y-1">
              {SECTIONS.map(({ id, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded transition-colors ${
                    activeSection === id
                      ? 'text-[#00ff88] bg-[#00ff88]/10'
                      : 'text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {activeSection === id && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                  <span className={activeSection === id ? 'ml-0' : 'ml-5'}>{title}</span>
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-6 mb-8 text-sm text-gray-400 leading-relaxed">
            Welcome to <strong className="text-white">ProfitSpot</strong> ("Service"), a DeFi yield
            intelligence platform. By accessing or using the Service, you agree to be bound by these
            Terms of Service. If you do not agree, do not use the Service.
          </div>

          <Section id="acceptance" title="Acceptance of Terms" icon={FileText}>
            <p>By creating an account or using ProfitSpot, you confirm that you:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Are at least 18 years of age</li>
              <li>Have the legal capacity to enter into a binding agreement</li>
              <li>Are not located in a jurisdiction where DeFi analytics tools are prohibited</li>
              <li>Have read and understood these Terms and our Privacy Policy</li>
            </ul>
            <p>If you are using the Service on behalf of an organization, you represent that you have the authority to bind that organization to these Terms.</p>
          </Section>

          <Section id="description" title="Description of Service" icon={Cpu}>
            <p>ProfitSpot provides DeFi yield intelligence tools, including:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><strong className="text-white">Yield Explorer</strong> — real-time APY data from DeFiLlama across 100+ protocols</li>
              <li><strong className="text-white">Net Profit Simulator</strong> — impermanent loss and gas cost modeling</li>
              <li><strong className="text-white">Smart Yield Alerts</strong> — ML-scored opportunities (Pro+)</li>
              <li><strong className="text-white">Whale Tracker</strong> — on-chain smart-money position monitoring (Elite)</li>
              <li><strong className="text-white">Advanced Backtesting</strong> — 4-scenario Monte Carlo projections (Elite)</li>
            </ul>
            <p>The Service is powered by a dual-node infrastructure (Nighton Intelligence Engine + Balinas Data Aggregator) and is intended for informational and educational purposes only.</p>
          </Section>

          <Section id="not-advice" title="Not Financial Advice" icon={AlertTriangle}>
            <Callout color="red">
              <p className="font-bold text-sm mb-1">This is not financial advice.</p>
              <p className="text-xs text-gray-400">
                All data, scores, ratings, alerts, simulations, and analyses provided by ProfitSpot are for
                informational purposes only. They do not constitute financial, investment, legal, or tax advice.
                ProfitSpot is not a registered investment advisor, broker-dealer, or financial institution.
              </p>
            </Callout>
            <p>You are solely and exclusively responsible for all investment and financial decisions you make. Any action you take based on information from ProfitSpot is strictly at your own risk. We strongly encourage you to consult qualified financial advisors before making any investment decisions.</p>
            <p>Past yields and performance data displayed on the platform do not guarantee, imply, or predict future results.</p>
          </Section>

          <Section id="non-custodial" title="Non-Custodial Nature" icon={Shield}>
            <Callout color="teal">
              <p className="font-semibold text-sm">ProfitSpot is entirely non-custodial.</p>
            </Callout>
            <p>We never hold, manage, control, or have access to your cryptocurrency assets, private keys, seed phrases, or wallet credentials. ProfitSpot does not:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Execute transactions on your behalf</li>
              <li>Custody any digital assets</li>
              <li>Have access to your wallet's private keys</li>
              <li>Store or transmit your seed phrases</li>
            </ul>
            <p>All interactions with DeFi protocols are performed directly by you using your own wallet. ProfitSpot is a read-only intelligence layer — we observe and analyze public blockchain data, we do not act on it.</p>
          </Section>

          <Section id="defi-risks" title="DeFi & Smart Contract Risks" icon={AlertTriangle}>
            <p>You acknowledge and accept that DeFi investments involve substantial risks, including but not limited to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li><strong className="text-white">Smart Contract Risk:</strong> Code vulnerabilities, exploits, and hacks can result in partial or total loss of funds</li>
              <li><strong className="text-white">Impermanent Loss:</strong> Providing liquidity to AMM pools can result in loss relative to simply holding assets</li>
              <li><strong className="text-white">Protocol Risk:</strong> DeFi protocols can be paused, exploited, or shut down without notice</li>
              <li><strong className="text-white">Regulatory Risk:</strong> DeFi regulations vary by jurisdiction and may change without notice</li>
              <li><strong className="text-white">Liquidity Risk:</strong> Pools may have insufficient liquidity for large positions</li>
              <li><strong className="text-white">Oracle Manipulation:</strong> Price feeds used by protocols can be manipulated</li>
              <li><strong className="text-white">Market Volatility:</strong> Cryptocurrency prices are highly volatile and can drop to zero</li>
            </ul>
            <p>ProfitSpot's security scores and risk ratings are algorithmic assessments from public data and are not guarantees of safety. Always conduct your own due diligence before interacting with any protocol.</p>
          </Section>

          <Section id="subscription" title="Subscription & Billing" icon={CreditCard}>
            <p>ProfitSpot offers the following paid subscription tiers:</p>
            <div className="grid grid-cols-3 gap-3 my-4">
              {[
                { name: 'Free', price: '$0', color: '#00d4ff' },
                { name: 'Pro',     price: '$29/mo', color: '#ffa502' },
                { name: 'Elite', price: '$79/mo', color: '#00ff88' },
              ].map(t => (
                <div key={t.name} className="bg-[#0a0a12] rounded-lg p-3 text-center border border-gray-800">
                  <div className="font-bold text-sm" style={{ color: t.color }}>{t.name}</div>
                  <div className="text-white font-bold mt-1">{t.price}</div>
                </div>
              ))}
            </div>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>All subscriptions are billed monthly and processed securely via <strong className="text-white">Stripe</strong></li>
              <li>Subscriptions auto-renew on the same date each month unless cancelled</li>
              <li>You may cancel at any time from your account settings; cancellation takes effect at the end of the current billing period</li>
              <li>Refund requests are considered on a case-by-case basis within 48 hours of a charge — contact support@profitspot.live</li>
              <li>We reserve the right to change pricing with 30 days' notice</li>
            </ul>
          </Section>

          <Section id="trial" title="Free Trial" icon={BookOpen}>
            <Callout color="teal">
              <p className="text-sm">New users receive a <strong>7-day free trial</strong> — no credit card required to start.</p>
            </Callout>
            <ul className="list-disc list-inside space-y-1 mt-3 text-gray-400">
              <li>Trial activates automatically upon account creation</li>
              <li>Trial provides access to Free-tier features (25 pools, security scores, Time-to-Profit charts)</li>
              <li>No automatic charge at trial end — upgrading requires explicit action</li>
              <li>One free trial per email address</li>
              <li>We reserve the right to modify or discontinue the trial offer</li>
            </ul>
          </Section>

          <Section id="prohibited" title="Prohibited Uses" icon={Ban}>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Scrape, crawl, or systematically extract data at scale without written permission</li>
              <li>Reverse engineer, decompile, or attempt to extract source code</li>
              <li>Violate any applicable law, regulation, or third-party right</li>
              <li>Attempt to gain unauthorized access to any accounts, systems, or networks</li>
              <li>Resell, sublicense, or redistribute ProfitSpot data or access</li>
              <li>Engage in market manipulation or use the Service to facilitate it</li>
              <li>Use automated means to access the Service beyond normal API usage</li>
              <li>Impersonate any person or entity</li>
            </ul>
            <p>Violations may result in immediate account termination and, where appropriate, legal action.</p>
          </Section>

          <Section id="ip" title="Intellectual Property" icon={FileText}>
            <p>All content, algorithms, designs, trademarks, and software on ProfitSpot — including the Nighton Intelligence Engine, yield scoring algorithms, and UI — are the exclusive property of ProfitSpot and protected by applicable intellectual property laws.</p>
            <p>You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes in accordance with these Terms.</p>
            <p>Nothing in these Terms transfers any intellectual property rights to you.</p>
          </Section>

          <Section id="disclaimers" title="Disclaimer of Warranties" icon={Scale}>
            <p className="font-mono text-xs text-gray-500 bg-[#0a0a12] rounded p-3 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.
              TO THE FULLEST EXTENT PERMITTED BY LAW, PROFITSPOT DISCLAIMS ALL WARRANTIES INCLUDING IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR THAT DATA WILL BE ACCURATE,
              COMPLETE, OR CURRENT. YIELD DATA SOURCED FROM THIRD PARTIES (DEFILLAMA, GOPLUS, MORALIS) IS PROVIDED AS-IS
              AND MAY CONTAIN INACCURACIES.
            </p>
          </Section>

          <Section id="liability" title="Limitation of Liability" icon={Scale}>
            <p className="font-mono text-xs text-gray-500 bg-[#0a0a12] rounded p-3 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PROFITSPOT AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
              AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
              INCLUDING BUT NOT LIMITED TO: FINANCIAL LOSSES FROM DEFI INVESTMENTS, LOSS OF DATA, LOSS OF PROFITS,
              SMART CONTRACT EXPLOITS, IMPERMANENT LOSS, OR ANY DAMAGES ARISING FROM YOUR USE OF OR INABILITY TO USE
              THE SERVICE — EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
              IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID TO US IN THE 3 MONTHS PRECEDING THE CLAIM.
            </p>
          </Section>

          <Section id="termination" title="Termination" icon={Ban}>
            <p>We reserve the right to suspend or terminate your account and access to the Service at any time, with or without notice, for:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Violation of these Terms</li>
              <li>Fraudulent, abusive, or illegal activity</li>
              <li>Non-payment of subscription fees</li>
              <li>Any other reason at our sole discretion</li>
            </ul>
            <p>You may cancel your account at any time. Upon termination, your right to use the Service ceases immediately. Sections covering IP, disclaimers, liability, and governing law survive termination.</p>
          </Section>

          <Section id="governing" title="Governing Law" icon={Gavel}>
            <p>These Terms are governed by and construed in accordance with the laws of the jurisdiction in which ProfitSpot operates, without regard to conflict of law principles.</p>
            <p>Any disputes arising from these Terms or your use of the Service shall first be attempted to be resolved through good-faith negotiation. If unresolved, disputes shall be subject to binding arbitration.</p>
            <p>You waive any right to participate in class-action lawsuits against ProfitSpot to the extent permitted by law.</p>
          </Section>

          <Section id="changes" title="Changes to Terms" icon={RefreshCw}>
            <p>We may update these Terms from time to time to reflect changes in the Service, law, or our practices. We will notify users of material changes via:</p>
            <ul className="list-disc list-inside space-y-1 text-gray-400">
              <li>Email to the address on your account</li>
              <li>A notice banner within the Service</li>
              <li>Updated "Last updated" date on this page</li>
            </ul>
            <p>Continued use of the Service after any changes constitutes your acceptance of the revised Terms.</p>
          </Section>

          <Section id="contact" title="Contact" icon={Mail}>
            <p>For questions, concerns, or requests relating to these Terms:</p>
            <div className="bg-[#0a0a12] rounded-lg p-4 border border-gray-800 mt-2">
              <p className="text-white font-medium">ProfitSpot Support</p>
              <p className="text-[#00ff88] mt-1">support@profitspot.live</p>
            </div>
          </Section>
        </main>
      </div>
    </div>
  );
}
